import type { NodeOutput } from "@/types/workflowEngine";
import {
  clampCrewRounds,
  parseManagerDecision,
  buildManagerSystemPrompt,
  type CrewWorker,
  type CrewDecision,
} from "./crewPure";

/**
 * Phase 4A.7 — crew.ai manager/delegation runtime.
 *
 * The manager LLM decomposes a goal and delegates ONE task at a time to the
 * best-suited worker, using the shared blackboard to decide the next step,
 * until it finishes (or the bounded round budget is exhausted). Deps are
 * injected so this is deterministic in tests; production wires them to the AI
 * proxy (manager) and executeAgentNode (workers).
 */

export interface CrewStep {
  round: number;
  worker: string;
  task: string;
  result: string;
}

export interface CrewResult {
  answer: string;
  steps: CrewStep[];
  rounds: number;
  terminatedBy: "finish" | "max_rounds" | "no_workers";
}

export interface CrewDeps {
  /** Ask the manager LLM for the next decision given the current system prompt. */
  callManager: (systemPrompt: string, goal: string) => Promise<string>;
  /** Run a worker with a task; returns the worker's raw output. */
  runWorker: (workerId: string, task: string) => Promise<string>;
  /** Optional progress hook. */
  onStep?: (step: CrewStep) => void;
}

export interface CrewConfig {
  goal: string;
  workers: CrewWorker[];
  maxRounds?: number;
  managerInstructions?: string;
}

export async function runCrew(config: CrewConfig, deps: CrewDeps): Promise<CrewResult> {
  const { goal, workers } = config;
  const steps: CrewStep[] = [];
  if (!workers.length) {
    // No workers → the manager just answers directly (single call).
    const sys = buildManagerSystemPrompt(goal, [], [], 1, config.managerInstructions);
    const raw = await deps.callManager(sys, goal);
    const decision = parseManagerDecision(raw, []);
    return {
      answer: decision.action === "finish" ? decision.answer : raw,
      steps,
      rounds: 0,
      terminatedBy: "no_workers",
    };
  }

  const validIds = workers.map((w) => w.id);
  const maxRounds = clampCrewRounds(config.maxRounds, workers.length);

  for (let round = 1; round <= maxRounds; round++) {
    const roundsLeft = maxRounds - round + 1;
    const sys = buildManagerSystemPrompt(goal, workers, steps, roundsLeft, config.managerInstructions);
    const raw = await deps.callManager(sys, goal);
    const decision: CrewDecision = parseManagerDecision(raw, validIds);

    if (decision.action === "finish") {
      return { answer: decision.answer, steps, rounds: round - 1, terminatedBy: "finish" };
    }

    // delegate
    let result: string;
    try {
      result = await deps.runWorker(decision.worker, decision.task);
    } catch (e) {
      result = `ERROR: ${(e as Error)?.message ?? "worker failed"}`;
    }
    const step: CrewStep = { round, worker: decision.worker, task: decision.task, result };
    steps.push(step);
    deps.onStep?.(step);
  }

  // Ran out of rounds — do a final synthesis call so we always return an answer.
  const sys = buildManagerSystemPrompt(
    goal,
    workers,
    steps,
    0,
    `${config.managerInstructions ?? ""}\nRound budget exhausted — you MUST finish now with your best synthesized answer.`,
  );
  const raw = await deps.callManager(sys, goal);
  const decision = parseManagerDecision(raw, validIds);
  return {
    answer: decision.action === "finish" ? decision.answer : raw,
    steps,
    rounds: maxRounds,
    terminatedBy: "max_rounds",
  };
}

/** Shape the crew result into the engine's NodeOutput contract. */
export function crewResultToNodeOutput(result: CrewResult): NodeOutput {
  return {
    raw: result.answer,
    parsed: {
      answer: result.answer,
      steps: result.steps,
      rounds: result.rounds,
      terminatedBy: result.terminatedBy,
    },
  };
}
